/**
 * Color conversion utilities for handling OKLCH and OKLab colors
 * Based on the OKLCH/OKLab color space specifications
 */

/**
 * Parse color-mix() function and convert to a simple RGB value
 * Handles: color-mix(in oklch, rgb(255, 189, 22) 5%, transparent)
 * @param colorMixStr The color-mix string
 * @returns RGB/RGBA color string
 */
export function parseColorMix(colorMixStr: string): string {
  try {
    // Match color-mix(in oklch, color1 percentage, color2)
    // or color-mix(in oklab, color1 percentage, color2)
    const match = colorMixStr.match(/color-mix\(\s*in\s+(?:oklch|oklab)\s*,\s*([^,]+?)\s+(\d+(?:\.\d+)?%?)\s*,\s*([^)]+)\s*\)/i);
    if (!match) {
      // Try simpler format without percentage on first color
      const simpleMatch = colorMixStr.match(/color-mix\(\s*in\s+(?:oklch|oklab)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/i);
      if (simpleMatch) {
        // 50/50 mix - just return first color for simplicity
        return simpleMatch[1].trim();
      }
      return 'rgba(128, 128, 128, 0.5)'; // Fallback
    }
    
    const color1 = match[1].trim();
    const percentage = parseFloat(match[2]);
    const color2 = match[3].trim();
    
    // For simplicity, if one color is transparent and percentage is low,
    // return the other color with reduced opacity
    if (color2.toLowerCase() === 'transparent') {
      // Extract RGB from color1 and apply percentage as opacity
      const rgbMatch = color1.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
      if (rgbMatch) {
        const opacity = percentage / 100;
        return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
      }
    }
    
    // For other cases, just return the first color (simplified)
    return color1;
  } catch (e) {
    console.warn('[color-utils] Failed to parse color-mix:', colorMixStr, e);
    return 'rgba(128, 128, 128, 0.5)';
  }
}

/**
 * Convert OKLCH color to RGB
 * @param l Lightness (0-1)
 * @param c Chroma (0-0.4 typically)
 * @param h Hue (0-360 degrees)
 * @returns RGB color as string in format 'rgb(r, g, b)'
 */
export function oklchToRgb(l: number, c: number, h: number): string {
  // Step 1: Convert OKLCH to OKLab
  const hRadians = h * Math.PI / 180;
  const a = c * Math.cos(hRadians);
  const b = c * Math.sin(hRadians);
  
  // Step 2: Convert OKLab to linear RGB
  // OKLab to linear sRGB transformation matrix
  const lrgb = oklabToLinearRgb(l, a, b);
  
  // Step 3: Apply gamma correction to convert linear RGB to sRGB
  const r = linearToSrgb(lrgb[0]);
  const g = linearToSrgb(lrgb[1]);
  const b_val = linearToSrgb(lrgb[2]);
  
  // Clamp values to valid RGB range (0-255)
  const r_int = Math.max(0, Math.min(255, Math.round(r * 255)));
  const g_int = Math.max(0, Math.min(255, Math.round(g * 255)));
  const b_int = Math.max(0, Math.min(255, Math.round(b_val * 255)));
  
  return `rgb(${r_int}, ${g_int}, ${b_int})`;
}

/**
 * Convert OKLab color to RGB
 * @param l Lightness (0-1)
 * @param a Green-red axis (-0.4 to 0.4 typically)
 * @param b Blue-yellow axis (-0.4 to 0.4 typically)
 * @returns RGB color as string in format 'rgb(r, g, b)'
 */
export function oklabToRgb(l: number, a: number, b: number): string {
  // Convert OKLab to linear RGB
  const lrgb = oklabToLinearRgb(l, a, b);
  
  // Apply gamma correction to convert linear RGB to sRGB
  const r = linearToSrgb(lrgb[0]);
  const g = linearToSrgb(lrgb[1]);
  const b_val = linearToSrgb(lrgb[2]);
  
  // Clamp values to valid RGB range (0-255)
  const r_int = Math.max(0, Math.min(255, Math.round(r * 255)));
  const g_int = Math.max(0, Math.min(255, Math.round(g * 255)));
  const b_int = Math.max(0, Math.min(255, Math.round(b_val * 255)));
  
  return `rgb(${r_int}, ${g_int}, ${b_int})`;
}

/**
 * Parse OKLab color string and convert to RGB
 * @param oklabStr OKLab color string like "oklab(0.7 0.1 -0.05)" or "oklab(0.556 0 0 / 0.5)"
 * @returns RGB color string
 */
export function parseOklabString(oklabStr: string): string {
  // Extract the parameters from the OKLab string
  // Supports: oklab(0.7 0.1 -0.05) or oklab(70% 0.1 -0.05) or with alpha like oklab(0.556 0 0 / 0.5)
  const match = oklabStr.match(/oklab\(\s*([\d.]+)(%)?\s+(-?[\d.]+)(%)?\s+(-?[\d.]+)(%)?(?:\s*\/\s*([\d.]+)(%)?)?\s*\)/i);
  if (!match) {
    console.warn('[color-utils] Failed to parse oklab string:', oklabStr);
    return 'rgb(128, 128, 128)'; // Default to gray
  }
  
  let l = parseFloat(match[1] || '0');
  const lIsPct = !!match[2];
  let a = parseFloat(match[3] || '0');
  const aIsPct = !!match[4];
  let b = parseFloat(match[5] || '0');
  const bIsPct = !!match[6];
  const alpha = match[7] ? parseFloat(match[7]) : undefined;
  const alphaIsPct = !!match[8];
  
  // Normalize percentages
  if (lIsPct) l = l / 100;
  if (aIsPct) a = a / 100 * 0.4; // OKLab a/b range is roughly -0.4 to 0.4
  if (bIsPct) b = b / 100 * 0.4;
  
  const rgb = oklabToRgb(l, a, b);
  
  // Handle alpha channel
  if (alpha !== undefined) {
    const alphaNorm = alphaIsPct ? alpha / 100 : alpha;
    const rgbMatch = rgb.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    if (rgbMatch) {
      return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${Math.max(0, Math.min(1, alphaNorm))})`;
    }
  }
  
  return rgb;
}

/**
 * Parse OKLCH color string and convert to RGB
 * @param oklchStr OKLCH color string like "oklch(0.7 0.1 322)"
 * @returns RGB color string
 */
export function parseOklchString(oklchStr: string): string {
  // Extract the parameters from the OKLCH string
  const match = oklchStr.match(/oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*\)/i);
  if (!match) {
    console.warn('[color-utils] Failed to parse oklch string:', oklchStr);
    return 'rgb(128, 128, 128)'; // Default to gray
  }
  
  const l = parseFloat(match[1] || '0');
  const c = parseFloat(match[2] || '0');
  const h = parseFloat(match[3] || '0');
  
  return oklchToRgb(l, c, h);
}

/**
 * Parse any OKLab/OKLCH color string and convert to RGB
 * @param colorStr Color string (oklab or oklch)
 * @returns RGB color string
 */
export function parseOkColorString(colorStr: string): string {
  if (colorStr.toLowerCase().startsWith('oklab')) {
    return parseOklabString(colorStr);
  } else if (colorStr.toLowerCase().startsWith('oklch')) {
    return parseOklchString(colorStr);
  }
  console.warn('[color-utils] Unknown color format:', colorStr);
  return 'rgb(128, 128, 128)';
}

/**
 * Convert from OKLab to linear RGB
 */
function oklabToLinearRgb(l: number, a: number, b_val: number): [number, number, number] {
  // OKLab to linear sRGB transformation matrix multiplication
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b_val;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b_val;
  const s_ = l - 0.0894841775 * a - 1.2914855480 * b_val;
  
  // OKLab to linear RGB
  const l_cubed = l_ * l_ * l_;
  const m_cubed = m_ * m_ * m_;
  const s_cubed = s_ * s_ * s_;
  
  const r = 4.0767416621 * l_cubed - 3.3077115913 * m_cubed + 0.2309699292 * s_cubed;
  const g = -1.2684380046 * l_cubed + 2.6097574011 * m_cubed - 0.3413193965 * s_cubed;
  const b_out = -0.0041960863 * l_cubed - 0.7034186147 * m_cubed + 1.7076147010 * s_cubed;
  
  return [r, g, b_out];
}

/**
 * Convert linear RGB to sRGB with gamma correction
 */
function linearToSrgb(c: number): number {
  if (c <= 0) return 0;
  if (c >= 1) return 1;
  
  if (c < 0.0031308) {
    return c * 12.92;
  } else {
    return 1.055 * Math.pow(c, 1/2.4) - 0.055;
  }
}

/**
 * Process HTML content to replace OKLCH/OKLab colors with RGB equivalents
 * @param html HTML content string
 * @returns Processed HTML with OKLCH/OKLab colors replaced by RGB
 */
export function processHtmlColors(html: string): string {
  // Replace both oklch and oklab color definitions
  return html
    .replace(/oklch\([^)]+\)/gi, (match) => parseOklchString(match))
    .replace(/oklab\([^)]+\)/gi, (match) => parseOklabString(match));
}

/**
 * Process all stylesheets in the document to replace OKLCH colors with RGB
 * @returns Object containing original rules and processed rules
 */
export function processStylesheets(): { originalRules: CSSRule[][]; processedRules: CSSRule[][] } {
  const originalRules: CSSRule[][] = [];
  const processedRules: CSSRule[][] = [];
  
  // Process each stylesheet in the document
  for (let i = 0; i < document.styleSheets.length; i++) {
    try {
      const styleSheet = document.styleSheets[i] as CSSStyleSheet;
      // Handle potential undefined cssRules with a safe fallback
      const cssRules = styleSheet?.cssRules || styleSheet?.rules;
      const rules = cssRules ? Array.from(cssRules) : [];
      originalRules.push([...rules]);
      
      // Process each rule in the stylesheet
      rules.forEach((rule) => {
        if (rule instanceof CSSStyleRule) {
          const styleText = rule.style.cssText;
          if (styleText.includes('oklch')) {
            // Replace OKLCH colors with RGB
            const newStyleText = styleText.replace(/color:\s*oklch\([^)]+\)/gi, (colorMatch: string) => {
              const rgbColor = parseOklchString(colorMatch.substring(colorMatch.indexOf('oklch')));
              return `color: ${rgbColor}`;
            });
            
            // Update the rule's style text
            rule.style.cssText = newStyleText;
          }
        }
      });
      
      processedRules.push(rules);
    } catch (e) {
      // console.warn('Error processing stylesheet:', e);
    }
  }
  
  return { originalRules, processedRules };
}

/**
 * Process DOM element and its children to replace all OKLCH colors with RGB
 * @param element The element to process
 */
export function processElementColors(element: HTMLElement): void {
  // Process this element
  processElementComputedStyle(element);
  
  // Process all child elements recursively
  const children = element.querySelectorAll('*');
  children.forEach(child => {
    if (child instanceof HTMLElement) {
      processElementComputedStyle(child);
    }
  });
}

/**
 * List of all CSS properties that might contain color values
 */
const COLOR_PROPERTIES = [
  'color', 'background-color', 'background', 'border-color', 'border-top-color', 'border-right-color',
  'border-bottom-color', 'border-left-color', 'outline-color', 'outline', 'text-decoration-color',
  'caret-color', 'fill', 'stroke', 'stop-color', 'flood-color', 'lighting-color',
  'box-shadow', 'text-shadow', 'accent-color', 'column-rule-color'
];

/**
 * Sanitize a CSS value by replacing oklch, oklab, and color-mix with RGB equivalents
 */
function sanitizeCssValue(value: string): string {
  let result = value;
  
  // First handle color-mix functions (they may contain oklch/oklab references)
  if (result.includes('color-mix')) {
    result = result.replace(/color-mix\([^)]+\)/gi, match => parseColorMix(match));
  }
  
  // Then handle standalone oklch/oklab
  if (result.includes('oklch')) {
    result = result.replace(/oklch\([^)]+\)/gi, match => parseOklchString(match));
  }
  if (result.includes('oklab')) {
    result = result.replace(/oklab\([^)]+\)/gi, match => parseOklabString(match));
  }
  
  return result;
}

/**
 * Process an element's computed style and replace any OKLCH/OKLab colors with RGB
 * @param element The element to process
 */
function processElementComputedStyle(element: HTMLElement): void {
  // Process inline style properties
  for (let i = 0; i < element.style.length; i++) {
    const property = element.style[i];
    if (typeof property !== 'string') continue;
    
    const value = element.style.getPropertyValue(property);
    if (!value || typeof value !== 'string') continue;
    
    // Check if value contains any problematic color functions
    if (value.includes('oklch') || value.includes('oklab') || value.includes('color-mix')) {
      const newValue = sanitizeCssValue(value);
      if (newValue !== value) {
        element.style.setProperty(property, newValue, 'important');
      }
    }
  }
  
  // Get computed styles
  const computedStyles = window.getComputedStyle(element);
  
  // Check ALL computed style properties for oklch/oklab/color-mix
  for (let i = 0; i < computedStyles.length; i++) {
    const property = computedStyles[i];
    if (typeof property !== 'string') continue;
    
    const value = computedStyles.getPropertyValue(property);
    if (!value || typeof value !== 'string') continue;
    
    // Check if value contains any problematic color functions
    if (value.includes('oklch') || value.includes('oklab') || value.includes('color-mix')) {
      const newValue = sanitizeCssValue(value);
      if (newValue !== value) {
        element.style.setProperty(property, newValue, 'important');
      }
    }
  }
}

/**
 * Get element identifier for logging
 */
function getElementIdentifier(el: HTMLElement): string {
  const tag = el.tagName?.toLowerCase() || 'unknown';
  const id = el.id ? `#${el.id}` : '';
  const classes = el.className && typeof el.className === 'string' 
    ? '.' + el.className.split(' ').filter(Boolean).slice(0, 2).join('.') 
    : '';
  return `${tag}${id}${classes}`;
}

/**
 * Check if a string contains problematic color functions
 */
function hasProblematicColors(str: string): boolean {
  return str.includes('oklch') || str.includes('oklab') || str.includes('color-mix');
}

/**
 * Checks if there are any remaining OKLCH/OKLab/color-mix colors in the element or its children
 * @param element Element to check
 * @returns Boolean indicating if problematic colors were found
 */
export function checkForRemainingOklchColors(element: HTMLElement): boolean {
  let foundProblematicColor = false;
  
  // Function to check element's HTML for problematic color strings
  const checkElementForProblematicColors = (el: HTMLElement) => {
    // Check innerHTML
    if (el.innerHTML && hasProblematicColors(el.innerHTML)) {
      foundProblematicColor = true;
    }
    
    // Check outerHTML (for inline styles on the element itself)
    if (el.outerHTML && hasProblematicColors(el.outerHTML)) {
      foundProblematicColor = true;
    }
    
    // Check computed styles
    const computedStyles = window.getComputedStyle(el);
    for (let i = 0; i < computedStyles.length; i++) {
      const prop = computedStyles[i];
      const value = computedStyles.getPropertyValue(prop);
      if (typeof value === 'string' && hasProblematicColors(value)) {
        foundProblematicColor = true;
      }
    }
    
    // Check attributes
    Array.from(el.attributes).forEach(attr => {
      if (typeof attr.value === 'string' && hasProblematicColors(attr.value)) {
        foundProblematicColor = true;
      }
    });
  };
  
  // Check the main element
  checkElementForProblematicColors(element);
  
  // Check all children recursively
  const allElements = element.querySelectorAll('*');
  allElements.forEach(el => {
    if (el instanceof HTMLElement) {
      checkElementForProblematicColors(el);
    }
  });
  
  return foundProblematicColor;
}

/**
 * Creates a clone of the element with all OKLCH/OKLab colors converted to RGB
 * @param element Element to clone and process
 * @returns A new element with all OKLCH/OKLab colors converted to RGB
 */
export function createRgbProcessedClone(element: HTMLElement): HTMLElement {
  // Clone the element
  const clone = element.cloneNode(true) as HTMLElement;
  
  // Process all colors in the clone
  processElementColors(clone);
  
  // Double check and force-process any element with remaining OKLCH/OKLab/color-mix colors
  const forceProcess = (el: HTMLElement) => {
    let html = el.outerHTML;
    let changed = false;
    
    // Handle color-mix first (it may contain oklch/oklab)
    if (html && html.includes('color-mix')) {
      html = html.replace(/color-mix\([^)]+\)/gi, match => {
        try {
          return parseColorMix(match);
        } catch (e) {
          console.warn(`[color-utils] Failed to convert color-mix in HTML:`, match, e);
          return 'rgba(128, 128, 128, 0.5)';
        }
      });
      changed = true;
    }
    
    if (html && html.includes('oklch')) {
      html = html.replace(/oklch\([^)]+\)/gi, match => {
        try {
          return parseOklchString(match);
        } catch (e) {
          console.warn(`[color-utils] Failed to convert OKLCH in HTML:`, match, e);
          return 'rgb(128, 128, 128)'; // Fallback to gray
        }
      });
      changed = true;
    }
    
    if (html && html.includes('oklab')) {
      html = html.replace(/oklab\([^)]+\)/gi, match => {
        try {
          return parseOklabString(match);
        } catch (e) {
          console.warn(`[color-utils] Failed to convert OKLab in HTML:`, match, e);
          return 'rgb(128, 128, 128)'; // Fallback to gray
        }
      });
      changed = true;
    }
    
    if (changed) {
      // Create a new element with the processed HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const newElement = tempDiv.firstElementChild as HTMLElement;
      
      if (newElement && el.parentNode) {
        el.parentNode.replaceChild(newElement, el);
      }
    }
  };
  
  // Check and force process any elements with remaining OKLCH/OKLab
  if (checkForRemainingOklchColors(clone)) {
    console.warn('[color-utils] Found remaining OKLCH/OKLab colors after initial processing, applying force processing');
    forceProcess(clone);
    
    // Process all child elements too
    const allElements = clone.querySelectorAll('*');
    allElements.forEach(el => {
      if (el instanceof HTMLElement) {
        forceProcess(el);
      }
    });
  }
  
  return clone;
}
